// One-rAF pub-sub scheduler. Every consumer that wants to run visual
// work on the browser's frame cadence subscribes here instead of
// calling requestAnimationFrame itself.
//
// Why this exists: the harness had 10+ independent rAF loops during
// playback (one per voice trace, two cycle phasors, each level meter,
// the shared now-ref, …). Each one incurred its own scheduling tax:
// setup / teardown on mount / unmount, a fresh closure chain per frame,
// and — critically — made it easy to orphan a loop when a component
// dependency array skipped a restart. One loop with N subscribers is
// strictly cheaper and easier to reason about.
//
// Subscribers are called synchronously in insertion order. They should
// be short — this is called at display refresh rate, so ~16ms budget
// per frame, shared.

type Tick = (now: number) => void;

const subscribers = new Set<Tick>();
let rafId = 0;

function loop(now: number): void {
  // Snapshot before iterating — if a subscriber unsubscribes itself
  // mid-iteration, the Set mutation during iteration is technically
  // allowed (Set iteration is snapshot-based for already-visited
  // elements), but defensive copy keeps behavior obvious.
  const frozen = Array.from(subscribers);
  for (const fn of frozen) {
    try {
      fn(now);
    } catch (e) {
      // A broken subscriber shouldn't take down the rest of the frame.
      // Log once per ticker run — the harness's generic error handler
      // also catches this if it bubbles.
      console.error('[sharedTicker] subscriber threw:', e);
    }
  }
  rafId = requestAnimationFrame(loop);
}

/** Register a per-frame tick. Returns an unsubscribe function. The
 *  shared rAF loop auto-starts on first subscribe and auto-stops when
 *  the last subscriber unsubscribes — no leaks when the app is idle. */
export function subscribeTick(fn: Tick): () => void {
  const wasEmpty = subscribers.size === 0;
  subscribers.add(fn);
  if (wasEmpty) {
    rafId = requestAnimationFrame(loop);
  }
  return () => {
    subscribers.delete(fn);
    if (subscribers.size === 0 && rafId !== 0) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };
}
